<template>
  <div
    class="bg-white dark:bg-n-700 rounded-lg shadow-lg border border-n-50 dark:border-n-600 flex flex-col max-h-full overflow-hidden"
  >
    <div class="flex justify-between items-start flex-shrink-0 bg-n-100 dark:bg-n-600 p-4">
      <div class="flex-1">
        <div class="flex items-center gap-2">
          <h3 class="font-semibold text-xl text-n-900 dark:text-n-0">
            <span class="font-bold">{{ order.code }}</span>
            <span v-if="order.tabName"> - {{ order.tabName }}</span>
          </h3>
          <span
            v-if="isExpo && order.printerName"
            class="text-xs px-2 py-0.5 rounded-full bg-v-100 dark:bg-v-900 text-v-500 dark:text-v-300 font-medium"
          >
            {{ order.printerName }}
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-1.5 mt-1">
          <l-tooltip v-if="sourceBadge" placement="bottom">
            <img
              v-if="sourceBadge.logo"
              :src="sourceBadge.logo"
              :alt="sourceBadge.label"
              class="w-7 h-7 rounded-full object-contain"
            />
            <div
              v-else
              class="w-7 h-7 rounded-full flex items-center justify-center"
              :class="sourceBadge.bgClass"
            >
              <l-icon :name="sourceBadge.icon" :class="sourceBadge.iconClass" size="small" />
            </div>
            <template #content>{{ sourceBadge.label }}</template>
          </l-tooltip>
          <div
            v-if="order.waiterName"
            class="flex items-center gap-1 px-2 py-0.5 rounded-full bg-n-200 dark:bg-n-500"
          >
            <l-icon name="user" class="text-n-600 dark:text-n-200" size="small" />
            <span class="text-xs font-medium text-n-700 dark:text-n-100">{{ order.waiterName }}</span>
          </div>
        </div>
        <div class="flex flex-col gap-1 mt-1 text-base text-n-700 dark:text-n-50">
          <p>
            {{ pickupTypeLabel }}
          </p>
          <p v-if="order.pickupTime">
            {{ t('order.pickup-time') }}: {{ format(new Date(order.pickupTime), 'HH:mm') }}
          </p>
        </div>
      </div>
      <div v-if="order.status !== 'completed' && order.status !== 'cancelled'" class="text-right">
        <div class="text-2xl font-bold tabular-nums" :class="timerColor">
          {{ elapsedTime }}
        </div>
      </div>
    </div>

    <div class="p-4 flex flex-col flex-1 min-h-0">
      <l-button
        class="w-full mb-3 flex-shrink-0"
        size="medium"
        :color="orderButtonTheme"
        @click="handleOrderStatusChange"
      >
        {{ orderActionLabel }}
      </l-button>

      <div
        v-if="order.note"
        class="mb-3 p-2 bg-y-100 dark:bg-y-900/30 border border-y-400 dark:border-y-600 rounded flex-shrink-0"
      >
        <p class="text-sm font-medium text-y-550 dark:text-y-400">{{ order.note }}</p>
      </div>

      <div class="space-y-3 overflow-y-auto flex-1 min-h-0">
        <div v-for="(products, course) in productsByCourse" :key="course" class="space-y-2">
          <div v-if="course" class="text-base font-bold text-n-900 dark:text-n-0 uppercase py-2">
            {{ course }}
          </div>
          <kitchen-order-product
            v-for="product in products"
            :key="product.id"
            :product="product"
            @status-change="handleProductStatusChange(product.id, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import logoDeliveroo from '@last/core-ui/components/assets/logo_deliveroo.png'
import logoGlovo from '@last/core-ui/components/assets/logo_glovo.png'
import logoJustEat from '@last/core-ui/components/assets/logo_flyt.png'
import logoUber from '@last/core-ui/components/assets/logo_uber.png'
import { LButton, LIcon, LTooltip } from '@last/core-ui/paprika'

import { useConfigStore } from '@/store/config'
import { useKitchenOrderStore } from '@/store/kitchenOrders'
import type { KitchenOrderMapped } from '@/types'

import KitchenOrderProduct from './KitchenProduct.vue'

type Props = {
  order: KitchenOrderMapped
  currentTime: Date
  isExpo?: boolean
}

const props = defineProps<Props>()

const kitchenOrderStore = useKitchenOrderStore()
const configStore = useConfigStore()
const { updateKitchenOrderStatus, updateKitchenProductStatus } = kitchenOrderStore
const { screenConfig } = storeToRefs(configStore)

const { t } = useI18n()

const productsByCourse = computed(() => {
  const grouped: Record<string, typeof props.order.mappedProducts> = {}

  for (const product of props.order.mappedProducts) {
    const course = product.course || 0
    if (!grouped[course]) {
      grouped[course] = []
    }
    grouped[course].push(product)
  }

  return grouped
})

const elapsedTime = computed(() => {
  const created = new Date(props.order.creationTime).getTime()
  const elapsed = Math.floor((props.currentTime.getTime() - created) / 1000)

  const minutes = Math.floor(elapsed / 60)
  const seconds = elapsed % 60

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
})

const timerColor = computed(() => {
  const created = new Date(props.order.creationTime).getTime()
  const elapsed = Math.floor((props.currentTime.getTime() - created) / 1000)
  const minutes = Math.floor(elapsed / 60)

  if (minutes < screenConfig.value.thresholds.warning) return 'text-n-700 dark:text-n-50'
  if (minutes < screenConfig.value.thresholds.critical) return 'text-y-550 dark:text-y-400'
  return 'text-r-dark dark:text-r-400'
})

const orderActionLabel = computed(() => {
  const status = props.order.status
  if (!status || status === 'pending') return t('order.start-preparing')
  if (status === 'ongoing') return t('order.mark-as-completed')
  if (status === 'completed') return t('order.recall')
  return t('order.completed')
})

const orderButtonTheme = computed(() => {
  const status = props.order.status
  if (!status || status === 'pending') return 'yellow'
  if (status === 'ongoing') return 'green'
  if (status === 'completed') return 'primary'
  return 'primary'
})

type SourceStyle = {
  label: string
  logo?: string
  icon: string
  bgClass: string
  iconClass: string
}

const SOURCE_STYLES: Record<string, SourceStyle> = {
  Glovo: { label: 'Glovo', logo: logoGlovo, icon: 'motorcycle', bgClass: '', iconClass: '' },
  UberEats: { label: 'Uber Eats', logo: logoUber, icon: 'lucide/shopping-bag', bgClass: '', iconClass: '' },
  JustEat: { label: 'Just Eat', logo: logoJustEat, icon: 'lucide/shopping-bag', bgClass: '', iconClass: '' },
  Deliveroo: { label: 'Deliveroo', logo: logoDeliveroo, icon: 'bike', bgClass: '', iconClass: '' },
  Shop: { label: 'Online', icon: 'lucide/globe', bgClass: 'bg-blue-100 dark:bg-blue-900/40', iconClass: 'text-blue-700 dark:text-blue-400' },
  QR: { label: 'QR', icon: 'qr-code', bgClass: 'bg-v-100 dark:bg-v-900/40', iconClass: 'text-v-500 dark:text-v-300' }
}

const sourceBadge = computed((): SourceStyle | null => {
  const source = props.order.source
  if (!source || source === 'pos' || source === 'Restaurant') return null
  if (source.includes('Glovo')) return SOURCE_STYLES.Glovo
  if (source.includes('Uber')) return SOURCE_STYLES.UberEats
  if (source.includes('JustEat')) return SOURCE_STYLES.JustEat
  if (source.includes('Deliveroo')) return SOURCE_STYLES.Deliveroo
  return SOURCE_STYLES[source] || { label: source, icon: 'lucide/store', bgClass: 'bg-n-200 dark:bg-n-600', iconClass: 'text-n-600 dark:text-n-200' }
})

const pickupTypeLabel = computed(() => {
  const pickupType = props.order.pickupType
  if (!pickupType) return t(`order.pickup-type.for-here`)
  if (pickupType === 'ownDelivery') return t(`order.pickup-type.delivery`)
  if (pickupType === 'delivery') return props.order.source
  return t(`order.pickup-type.takeAway`)
})

function handleOrderStatusChange() {
  const status = props.order.status
  if (!status || status === 'pending') {
    updateKitchenOrderStatus(props.order.id, 'ongoing')
  } else if (status === 'ongoing') {
    updateKitchenOrderStatus(props.order.id, 'completed')
  } else if (status === 'completed') {
    updateKitchenOrderStatus(props.order.id, 'pending')
  }
}

function handleProductStatusChange(
  productId: string,
  newStatus: 'pending' | 'ongoing' | 'completed' | 'cancelled' | null
) {
  updateKitchenProductStatus(props.order.id, productId, newStatus)
}
</script>
